import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import type { EpisodeLupaPeriodResponse } from '../services/api';

type LupaPeriodStatus = EpisodeLupaPeriodResponse['status'];
// `missing_inputs` is optional in the generated schema, so index its non-null
// form to get the element type.
type MissingInput = NonNullable<
  EpisodeLupaPeriodResponse['missing_inputs']
>[number];

interface LupaThresholdGaugeProps {
  periods: EpisodeLupaPeriodResponse[];
}

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];

/**
 * "1st period". Not "1st 30-day period": under a heading that already says
 * LUPA thresholds, every period is a 30-day one, and the four words pushed the
 * row's chips and count onto a second line beside the calendar (EMR-1161).
 */
const periodName = (periodNumber: number): string =>
  `${ORDINALS[periodNumber - 1] ?? `${periodNumber}th`} period`;

/**
 * The hatched fill for the bar's uncertainty band — the span between the lowest
 * and highest threshold the period could still land on.
 */
const HATCH_FILL =
  'bg-[repeating-linear-gradient(45deg,rgba(107,114,128,0.35)_0_3px,transparent_3px_6px)]';

// No dedicated success/warning token in the design system, so status colors use
// the Tailwind palette for clear/at-risk and the --destructive token for LUPA.
// Static class strings (no interpolation) so Tailwind keeps them.
//
// Status color rides on the bar and its status line only. The card tints these
// used to carry were dropped with the cards (EMR-1161): beside the calendar the
// strip has to read as a light list of bars, and a full-width tint per period
// is the heaviest thing on the page.
const STATUS_STYLE: Record<LupaPeriodStatus, { bar: string; accent: string }> =
  {
    clear: { bar: 'bg-emerald-600', accent: 'text-emerald-700' },
    at_risk: { bar: 'bg-amber-500', accent: 'text-amber-700' },
    lupa: { bar: 'bg-destructive', accent: 'text-destructive' },
    unknown: { bar: 'bg-gray-300', accent: 'text-gray-700' },
  };

/**
 * Why a period's value is not settled yet, most upstream first. The first match
 * is the one shown: a period with no diagnoses coded is not usefully described
 * as "waiting on signature".
 */
const MISSING_INPUT_PRIORITY: MissingInput[] = [
  'primary_diagnosis',
  'oasis_assessment',
  'oasis_answers',
  'diagnosis_coding',
  'signature',
];

const MISSING_INPUT_LABEL: Record<MissingInput, string> = {
  primary_diagnosis: 'No primary diagnosis',
  oasis_assessment: 'No OASIS assessment',
  oasis_answers: 'OASIS not finished',
  diagnosis_coding: 'Diagnoses can still change',
  signature: 'Waiting on signature',
};

const primaryMissingInput = (
  period: EpisodeLupaPeriodResponse
): MissingInput | null => {
  // Optional in the generated schema, so treat an absent list as "nothing known
  // to be outstanding" rather than throwing on a payload without it.
  const missing = period.missing_inputs ?? [];
  return MISSING_INPUT_PRIORITY.find(cause => missing.includes(cause)) ?? null;
};

/** The OASIS items with no response yet, absent list treated as none. */
const unansweredItems = (period: EpisodeLupaPeriodResponse): string[] =>
  period.unanswered_items ?? [];

/**
 * The parenthetical after the period's name ("1st 30-day period (No primary
 * diagnosis)"). Estimated states name themselves so the number beside them is
 * never read as settled; a final period says nothing at all.
 *
 * Always a short bucket phrase, never the backend's `detail`. `detail` is
 * free text the grouper wrote — an M1033 conflict runs to a couple of hundred
 * characters — and this label ends up in a `whitespace-nowrap` chip inside a
 * fixed-width column, where a sentence overflows the page instead of wrapping.
 * The reason in the backend's own words is still shown; it lives in the
 * popover, which has the room for it.
 */
const causeLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  if (period.certainty === 'final') {
    return null;
  }
  const cause = primaryMissingInput(period);
  if (!cause) {
    // An unavailable period with no named cause is the estimate pipeline
    // failing, not a data gap the user can close: the schema service is down,
    // the episode has no admission row, the grouper refused (see
    // `_safe_period_estimate`, which withholds a cause on purpose rather than
    // name the wrong one). That row has no threshold and so no count either,
    // which left it rendering as a period name over an empty track — nothing
    // at all to tell a scheduler the blank is the system's problem. Vague
    // because the backend genuinely cannot say more here — but it reads as a
    // fault rather than as a wait, so it is not mistaken for a period whose
    // inputs simply have not landed yet.
    return period.certainty === 'unavailable' ? "Couldn't calculate" : null;
  }
  if (cause === 'primary_diagnosis' && period.detail) {
    // A `primary_diagnosis` cause covers two different situations, and the
    // bucket label only fits one of them. The backend also files "CMS refuses
    // to group the coded principal" here (`UngroupablePrincipalDiagnosisError`
    // in `hipps_estimate.py`), and it always attaches a `detail` when it does.
    // "No primary diagnosis" would send a scheduler hunting for a diagnosis
    // that is already coded, so name the actual problem instead. Still a short
    // bucket phrase, not the `detail` itself — see the docblock above.
    return "Diagnosis won't group";
  }
  // The bare reason, with no "Estimated —" / "Not available yet —" prefix: the
  // period already carries an Estimated chip or a `?`-masked HIPPS pattern, and
  // an unavailable period has no number to qualify at all (EMR-1161). The
  // prefix was the same word twice on a two-line row.
  return MISSING_INPUT_LABEL[cause];
};

/** "4", or "3–4" when the threshold is known only to within a range. */
const thresholdLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  const { lupa_threshold_low: low, lupa_threshold_high: high } = period;
  if (low == null || high == null) {
    return null;
  }
  return low === high ? `${low}` : `${low}–${high}`;
};

/**
 * CMS publishes case-mix weights to four decimals, and the product already
 * shows them that way on the coding page's Payout Weight card. Same number,
 * same shape, so a coder moving between the two screens reads one value.
 */
const formatWeight = (weight: number): string => weight.toFixed(4);

/**
 * "×0.9973", or "×0.8689–1.0804" while the code can still move (EMR-1019).
 *
 * The `×` is doing real work: the weight is a multiplier on CMS's standard
 * 30-day rate, and the glyph says so before the popover's sentence spells it
 * out. It also keeps the number from reading as a sixth HIPPS position.
 *
 * Ranges are the common case here, not the exception. CMS prices 432 codes
 * with 4 distinct LUPA thresholds but 412 distinct weights, so a period whose
 * positions are still open will nearly always report a span even when its
 * threshold has already settled to one number.
 */
const weightLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  const { case_mix_weight_low: low, case_mix_weight_high: high } = period;
  if (low == null || high == null) {
    return null;
  }
  return low === high
    ? `×${formatWeight(low)}`
    : `×${formatWeight(low)}–${formatWeight(high)}`;
};

/**
 * Descriptive, never imperative. This gauge is read by schedulers, and a line
 * telling them to add N visits to reach a payment threshold reads as an
 * instruction to schedule for payment rather than for clinical need. The visit
 * count and the threshold carry the same information without saying that.
 */
const statusLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  const threshold = thresholdLabel(period);
  if (threshold == null) {
    // Nothing to say: with no threshold there is no count to place against it,
    // and the cause line below names what is missing.
    return null;
  }
  // The count carries the state on its own against the threshold beside it, so
  // only the two ends are named (EMR-1161): "met", and the closed-and-short
  // "LUPA" that changes how the period is paid. An open period short of its
  // threshold is just its count — the amber bar already says it is not there
  // yet, and "below threshold" restated the two numbers in words.
  const counts = `${period.billable_visit_count} of ${threshold}`;
  if (period.status === 'clear') {
    return `${counts} · met`;
  }
  if (period.status === 'lupa') {
    return `${counts} · LUPA`;
  }
  return counts;
};

/**
 * The `?` positions in a HIPPS pattern, named in the words a scheduler would
 * use to find the unanswered thing. Pattern index 1 (the clinical group) is
 * left out on purpose: it is set by the primary diagnosis, which index 3's
 * "diagnosis coding" already sends the reader to, and naming it twice would
 * read as two separate gaps.
 */
const STILL_OPEN_BY_POSITION: { index: number; label: string }[] = [
  {
    index: 0,
    label: 'How the patient was admitted (inpatient-discharge questions)',
  },
  { index: 2, label: 'Functional ability questions' },
  { index: 3, label: 'Diagnosis coding' },
];

const stillOpenItems = (period: EpisodeLupaPeriodResponse): string[] => {
  const pattern = period.hipps_pattern;
  if (!pattern) {
    return [];
  }
  return STILL_OPEN_BY_POSITION.filter(
    ({ index }) => pattern[index] === '?'
  ).map(({ label }) => label);
};

/**
 * What the counts already settle, even while the threshold does not. The
 * takeaway of the popover, and the only line in it set in a darker weight.
 *
 * Descriptive arithmetic only — the same rule the card's status line follows.
 * "This period does not yet have enough visits" is a statement about the
 * period; "schedule three more visits" would be an instruction to schedule for
 * payment.
 */
const outcomeSentence = (period: EpisodeLupaPeriodResponse): string | null => {
  const {
    lupa_threshold_low: low,
    lupa_threshold_high: high,
    billable_visit_count: count,
  } = period;
  if (high != null && high > 0 && count >= high) {
    return (
      `This period has ${count} billable visits. That clears every possible ` +
      'threshold.'
    );
  }
  if (low != null && count < low) {
    return 'This period does not yet have enough visits for any possible threshold.';
  }
  return null;
};

/**
 * Per-30-day-period LUPA gauge for the episode-manager calendar (ROG-2493,
 * EMR-820).
 *
 * Presentational. Each period is one row: the period name and its HIPPS on the
 * left, the visit count against the threshold on the right, and a progress bar
 * under both. Every row uses the same furniture — the track is always grey and
 * the bar always carries its status colour, settled or not. Narrowed to fit a
 * 224px column (the panel's `xl:w-56` strip) beside the calendar rather than
 * the full-width card it used to be (EMR-1161).
 *
 * Uncertainty is carried by three things instead, so it is legible before any
 * word is read: the HIPPS is masked (`3H??1`) where digits can still move, a
 * hatched band covers the span between the lowest and highest possible
 * threshold, and a dashed chip names the state ("Estimated", or the cause
 * itself when there is no threshold to qualify).
 *
 * The HIPPS sits beside the period name rather than in the popover: it is the
 * code everything else on the row follows from, and a popover holding only a
 * copy of something already visible is an empty popover with extra steps. The
 * case-mix weight lives *behind* the code, in a popover the code itself opens
 * (EMR-1019). It was inline once, and at 224px it wrapped every row it touched
 * — and a visible money number beside a visit count also read to schedulers as
 * a reason to schedule, which this gauge refuses everywhere else. Behind the
 * code, the coder who changes a diagnosis is one click from what the change is
 * worth, and everyone else sees one line per period. The dotted underline is
 * the cue that the code has something to say; the popover's CMS term
 * ("case-mix weight") is itself the cue for anyone else that the number is not
 * theirs to act on.
 *
 * The gauge always renders, including when no threshold is determinable. An
 * empty space asks a question the user cannot answer; a named cause answers it.
 *
 * The chip is the sole popover trigger — there is no separate "?" icon, which
 * read as a second control doing something else. Behind it: the cause (when the
 * chip did not already say it), the questions still open, what the count
 * settles regardless of how they are answered, and the backend's own words. A
 * period with nothing to add has an inert chip and no popover at all.
 *
 * It stays in the same descriptive register as the card: it explains the
 * arithmetic, never prescribes a visit.
 */
const LupaThresholdGauge: React.FC<LupaThresholdGaugeProps> = ({ periods }) => {
  if (periods.length === 0) {
    return null;
  }

  return (
    <div className='space-y-3'>
      {periods.map(period => {
        const style = STATUS_STYLE[period.status];
        const hippsLabel = period.hipps_code ?? period.hipps_pattern;
        const weight = weightLabel(period);

        // The bar is scaled to the highest possible threshold, so a period can
        // only look complete once it clears every candidate.
        const high = period.lupa_threshold_high ?? 0;
        const low = period.lupa_threshold_low ?? high;
        const fillPct =
          high > 0
            ? Math.min(
                100,
                Math.round((period.billable_visit_count / high) * 100)
              )
            : 0;
        // Where the uncertain part of the target begins.
        const bandStartPct = high > 0 ? Math.round((low / high) * 100) : 100;
        const hasRange = high > 0 && low !== high;
        const status = statusLabel(period);
        const cause = causeLabel(period);

        const stillOpen =
          period.certainty === 'estimated' ? stillOpenItems(period) : [];
        const outcome =
          period.certainty === 'estimated' ? outcomeSentence(period) : null;
        const unanswered = unansweredItems(period);
        // The one mark a row carries besides its count, and the thing you
        // click for the rest (EMR-1161). A settled period has none: its number
        // is the whole story. Everything else is "Estimated", except a period
        // with no number at all, where the chip is the reason there isn't one
        // — the chip is the row's state, whatever that state is, so there is
        // never a second differently-shaped affordance beside it.
        const chipLabel =
          period.certainty === 'final'
            ? null
            : status == null
              ? cause
              : 'Estimated';
        // Said once: on the chip, or in the popover, never both.
        const popoverCause = chipLabel === cause ? null : cause;
        // A chip only becomes a button when there is something behind it. A
        // period whose chip already is its whole explanation stays inert
        // rather than opening an empty card (EMR-1161).
        // The HIPPS is no longer among these: it is on the row itself now, and
        // a popover holding only a copy of something already visible is an
        // empty popover with extra steps.
        const hasPopoverContent =
          popoverCause != null ||
          stillOpen.length > 0 ||
          outcome != null ||
          unanswered.length > 0 ||
          !!period.detail;

        return (
          <div key={period.period_number} data-certainty={period.certainty}>
            <div className='mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1'>
              {/* One step down from the section heading above it: this names a
                  row, not the block (EMR-1161). */}
              <span className='flex flex-wrap items-center gap-1.5 text-xs font-medium text-gray-600'>
                {periodName(period.period_number)}
                {/* The code the row follows from, beside the name it belongs
                    to. Mono so it reads as a code, but in the period name's
                    own colour and size — it labels the row rather than
                    competing with the count on the right, and a masked
                    pattern (3H??1) still shows what is settled. */}
                {hippsLabel && !weight && (
                  <span className='font-mono'>{hippsLabel}</span>
                )}
                {/* When the table prices the code, the code itself opens the
                    case-mix weight (EMR-1019) — a click-popover like the chip,
                    not a bare title, so it works on touch and announces itself
                    with the dotted underline. The weight stays reachable on a
                    LUPA period, where CMS discards it and pays per visit: the
                    code on the claim is real either way, and the "· LUPA" on
                    the row already says the weight was not earned. */}
                {hippsLabel && weight && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type='button'
                        aria-label={`HIPPS ${hippsLabel} — case-mix weight`}
                        className='font-mono text-gray-600 underline decoration-gray-400 decoration-dotted underline-offset-2 transition-colors hover:text-gray-800 hover:decoration-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1'
                      >
                        {hippsLabel}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align='start'
                      data-testid={`hipps-weight-${period.period_number}`}
                      className='w-72 max-w-[calc(100vw-2rem)] space-y-1 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600'
                    >
                      {/* CMS's own name for the number, on purpose: a coder
                          knows the term from PDGM training, and to anyone
                          else it reads as billing jargon — which is the cue
                          that the value is not theirs to act on. The last
                          sentence says so outright, descriptively. */}
                      <p className='font-medium text-gray-700'>
                        Case-mix weight{' '}
                        <span className='font-mono'>{weight}</span>
                      </p>
                      <p>
                        CMS multiplies its standard 30-day rate by this. It is
                        set by the HIPPS code. Billing uses it; scheduling does
                        not.
                      </p>
                    </PopoverContent>
                  </Popover>
                )}
                {/* One shape for every row that has anything to explain: the
                    state chip is the button (EMR-1161). A "?" icon beside a
                    chip that opened the same popover read as two different
                    controls doing two different things. */}
                {chipLabel && !hasPopoverContent && (
                  <span className='whitespace-nowrap rounded-full border border-dashed border-gray-400 px-1 py-0 text-[0.65rem] font-semibold leading-4 tracking-normal text-gray-600'>
                    {chipLabel}
                  </span>
                )}
                {chipLabel && hasPopoverContent && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type='button'
                        aria-label={`${chipLabel} — what this period is waiting on`}
                        className='whitespace-nowrap rounded-full border border-dashed border-gray-400 px-1 py-0 text-[0.65rem] font-semibold leading-4 tracking-normal text-gray-600 transition-colors hover:border-gray-500 hover:bg-gray-50 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1'
                      >
                        {chipLabel}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align='start'
                      data-testid={`lupa-help-${period.period_number}`}
                      className='w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600'
                    >
                      {/* What the period is waiting on, in the words the row
                          no longer has space for (EMR-1161). */}
                      {popoverCause && <p>{popoverCause}</p>}
                      {stillOpen.length > 0 && (
                        <div className='space-y-0.5'>
                          <p>Still open:</p>
                          <ul className='list-disc space-y-0.5 pl-4'>
                            {stillOpen.map(item => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {unanswered.length > 0 && (
                        <div className='space-y-0.5'>
                          <p>No response yet:</p>
                          <ul className='list-disc space-y-0.5 pl-4'>
                            {unanswered.map(item => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* The grouper's own words for the gap. Only ever here:
                          it is free text of any length, and the chip that
                          opens this card cannot wrap (EMR-1161). */}
                      {period.detail && (
                        <p className='font-medium text-gray-700'>
                          {period.detail}
                        </p>
                      )}
                      {outcome && (
                        <p className='font-medium text-gray-700'>{outcome}</p>
                      )}
                    </PopoverContent>
                  </Popover>
                )}
              </span>
              {/* The count. A period with no threshold has none, and its chip
                  is already the reason why (EMR-1161). */}
              {status && (
                <span className={`whitespace-nowrap text-xs ${style.accent}`}>
                  {status}
                </span>
              )}
            </div>

            {/* Same bar as the authorization strip beside it — one plain track
                for both, so an empty LUPA bar and an empty authorization bar
                are not two different-looking things (EMR-1161).

                The bar carries its status color even while the value is still
                an estimate. Greying it made the strip's most-scanned signal —
                is this period on track — unreadable on exactly the periods
                that are still open, which are the ones a scheduler can act on.
                The dashed chip, the `?`-masked HIPPS, and the hatched range
                band still mark the value as unsettled. */}
            <div className='relative h-1.5 overflow-hidden rounded-full bg-gray-100'>
              {hasRange && (
                <div
                  data-testid={`lupa-range-band-${period.period_number}`}
                  className={`absolute inset-y-0 ${HATCH_FILL}`}
                  style={{
                    left: `${bandStartPct}%`,
                    right: 0,
                  }}
                />
              )}
              <div
                className={`h-full rounded-full ${style.bar}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LupaThresholdGauge;
